import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, PageTitle, GameContainer, Button, FlexContainer, DraggableItem, DropZone } from '../../styles/StyledComponents';
import { useGameContext } from '../../context/GameContext';

const ScrambleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 2rem 0;
`;

const PictureClue = styled.div`
  width: 200px;
  height: 200px;
  background-color: #f9f9f9;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  font-size: 6rem;
  box-shadow: var(--box-shadow);
`;

const WordsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 1rem 0;
  min-height: 100px;
`;

const SentenceArea = styled.div`
  width: 100%;
  background-color: #f9f9f9;
  border-radius: var(--border-radius);
  padding: 1rem;
  margin: 1rem 0;
  min-height: 100px;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  box-shadow: var(--box-shadow);
`;

const Word = styled.div<{ isDragging?: boolean, isInSentence?: boolean, isCorrect?: boolean | null }>`
  padding: 0.75rem 1.5rem;
  margin: 0.5rem;
  background-color: ${props => {
    if (props.isInSentence) {
      if (props.isCorrect === true) return 'var(--success-color)';
      if (props.isCorrect === false) return 'var(--error-color)';
      return 'var(--secondary-color)';
    }
    return props.isDragging ? 'var(--secondary-color)' : 'white';
  }};
  border: 3px solid var(--secondary-color);
  border-radius: var(--border-radius);
  cursor: ${props => props.isInSentence ? 'default' : 'grab'};
  user-select: none;
  transition: all 0.2s ease;
  box-shadow: ${props => (props.isDragging ? 'var(--box-shadow)' : '0 4px 0 var(--secondary-color)')};
  font-size: 1.5rem;
  color: ${props => {
    if (props.isInSentence) {
      if (props.isCorrect !== null) return 'white';
      return 'var(--text-color)';
    }
    return props.isDragging ? 'white' : 'var(--text-color)';
  }};

  &:active {
    cursor: grabbing;
  }
`;

const FeedbackMessage = styled.div<{ isCorrect: boolean }>`
  font-size: 1.5rem;
  text-align: center;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: var(--border-radius);
  background-color: ${props => props.isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'};
  color: ${props => props.isCorrect ? 'var(--success-color)' : 'var(--error-color)'};
`;

const ScoreDisplay = styled.div`
  font-size: 1.2rem;
  text-align: right;
  margin-bottom: 1rem;

  span {
    font-weight: bold;
    color: var(--primary-color);
  }
`;

const InstructionText = styled.p`
  font-size: 1.3rem;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const CheckButton = styled(Button)`
  margin-top: 1rem;
`;

interface Sentence {
  words: string[];
  clue: string;
}

const sentences: Sentence[] = [
  { words: ['The', 'cat', 'runs', 'fast'], clue: '🐱' },
  { words: ['I', 'like', 'to', 'play'], clue: '🎮' },
  { words: ['Dogs', 'bark', 'loudly', 'outside'], clue: '🐶' },
  { words: ['The', 'bird', 'flies', 'high'], clue: '🐦' },
  { words: ['Frogs', 'jump', 'in', 'ponds'], clue: '🐸' },
  { words: ['Fish', 'swim', 'in', 'water'], clue: '🐠' },
  { words: ['Bears', 'eat', 'honey', 'happily'], clue: '🐻' },
  { words: ['Monkeys', 'climb', 'tall', 'trees'], clue: '🐵' },
  { words: ['We', 'read', 'fun', 'books'], clue: '📚' },
  { words: ['They', 'play', 'in', 'parks'], clue: '🏞️' }
];

const SentenceScramble: React.FC = () => {
  const [currentSentence, setCurrentSentence] = useState<Sentence>(getRandomSentence());
  const [scrambledWords, setScrambledWords] = useState<string[]>([]);
  const [sentenceWords, setSentenceWords] = useState<string[]>([]);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState<number>(0);
  const [draggingWord, setDraggingWord] = useState<string | null>(null);
  const { updateProgress } = useGameContext();

  function getRandomSentence(): Sentence {
    return sentences[Math.floor(Math.random() * sentences.length)];
  }

  // Scramble the words when the current sentence changes
  useEffect(() => {
    const words = [...currentSentence.words];
    // Shuffle the words
    for (let i = words.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [words[i], words[j]] = [words[j], words[i]];
    }
    setScrambledWords(words);
    setSentenceWords([]);
    setIsCorrect(null);
  }, [currentSentence]);

  const handleDragStart = (word: string) => {
    setDraggingWord(word);
  };

  const handleDrop = () => {
    if (draggingWord && scrambledWords.includes(draggingWord)) {
      // Move the word from scrambled to sentence
      setScrambledWords(prev => prev.filter(w => w !== draggingWord));
      setSentenceWords(prev => [...prev, draggingWord]);
      setDraggingWord(null);
    }
  };

  const handleWordClick = (word: string) => {
    if (scrambledWords.includes(word)) {
      // Move the word from scrambled to sentence
      setScrambledWords(prev => prev.filter(w => w !== word));
      setSentenceWords(prev => [...prev, word]);
    }
  };

  const handleCheckSentence = () => {
    // Check if the sentence is correct
    const correct = sentenceWords.join(' ') === currentSentence.words.join(' ');
    setIsCorrect(correct);

    if (correct) {
      setScore(prevScore => prevScore + 1);
      playSound('success');

      // Move to next sentence after 2 seconds
      setTimeout(() => {
        setCurrentSentence(getRandomSentence());
      }, 2000);
    } else {
      playSound('error');
    }

    // Save progress to context
    updateProgress('sentence-scramble', score + (correct ? 1 : 0));
  };

  const handleReset = () => {
    // Reset the current sentence
    const words = [...currentSentence.words];
    for (let i = words.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [words[i], words[j]] = [words[j], words[i]];
    }
    setScrambledWords(words);
    setSentenceWords([]);
    setIsCorrect(null);
  };

  const playSound = (type: 'success' | 'error' | 'click') => {
    // In a real implementation, we would play actual sounds here
    console.log(`Playing ${type} sound`);
  };

  return (
    <Container>
      <PageTitle>Sentence Scramble</PageTitle>

      <GameContainer>
        <FlexContainer justify="space-between">
          <Link to="/">
            <Button variant="secondary">Back to Games</Button>
          </Link>

          <ScoreDisplay>
            Score: <span>{score}</span>
          </ScoreDisplay>
        </FlexContainer>

        <InstructionText>
          Unscramble the words to form a sentence!
        </InstructionText>

        <ScrambleContainer>
          <PictureClue>{currentSentence.clue}</PictureClue>

          <SentenceArea>
            {sentenceWords.length > 0 ? (
              sentenceWords.map((word, index) => (
                <Word
                  key={index}
                  isInSentence={true}
                  isCorrect={isCorrect}
                >
                  {word}
                </Word>
              ))
            ) : (
              <InstructionText>Drag words here to form a sentence</InstructionText>
            )}
          </SentenceArea>

          <WordsContainer>
            {scrambledWords.map((word, index) => (
              <Word
                key={index}
                isDragging={draggingWord === word}
                onClick={() => handleWordClick(word)}
                onMouseDown={() => handleDragStart(word)}
              >
                {word}
              </Word>
            ))}
          </WordsContainer>

          <FlexContainer>
            <Button variant="secondary" onClick={handleReset} style={{ marginRight: '1rem' }}>
              Reset
            </Button>
            <Button
              onClick={handleCheckSentence}
              disabled={sentenceWords.length !== currentSentence.words.length || isCorrect !== null}
            >
              Check Sentence
            </Button>
          </FlexContainer>

          {isCorrect !== null && (
            <FeedbackMessage isCorrect={isCorrect}>
              {isCorrect
                ? '✨ Great job! That\'s correct! ✨'
                : 'Try again! The words are not in the right order.'}
            </FeedbackMessage>
          )}
        </ScrambleContainer>
      </GameContainer>
    </Container>
  );
};

export default SentenceScramble;
